import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
} from '@angular/core';

export interface ChildSelectorProfile {
  id: string;
  name: string;
  birthDate?: string;
  photoURL?: string;
  gender?: 'boy' | 'girl';
}

/**
 * Child/protagonist selector — pill-style tab switcher.
 * Dumb component: receives child list and selected id, emits selection.
 * Selected state is applied directly on each button (CSS, no JS timing issues).
 *
 * @example
 * <om-child-selector
 *   [children]="children()"
 *   [selectedId]="selectedChildId()"
 *   (selectedIdChange)="onChildSelect($event)"
 *   (addAction)="onAddChild()"
 * />
 */
@Component({
  selector: 'om-child-selector',
  templateUrl: './child-selector.html',
  styleUrl: './child-selector.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChildSelector {
  /** List of children/protagonists */
  readonly children = input<ChildSelectorProfile[]>([]);

  /** Currently selected child ID */
  readonly selectedId = input<string>('');

  /** Whether to show the "add child" button */
  readonly showAddButton = input(true);

  /** Emits the id of the child the user clicked */
  readonly selectedIdChange = output<string>();

  /** Emits when the user clicks the add button */
  readonly addAction = output<void>();

  /** Display the currently selected child */
  readonly selectedChild = computed(() =>
    this.children().find((c) => c.id === this.selectedId())
  );

  selectChild(id: string, btn: HTMLElement): void {
    this.selectedIdChange.emit(id);
    btn.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
  }
}
